import { useState } from 'react';
import type { CSSProperties } from 'react';
import type { Bookmark } from '../../types/api';
import { ThumbnailPlaceholderIcon } from '../Bookmark/Row/BookmarkThumbnail';

interface SelectionStackProps {
  bookmarks: Bookmark[];
  showThumbnails: boolean;
  badgeCount?: number;
  allowPlaceholderFallback?: boolean;
}

/* eslint-disable lingui/no-unlocalized-strings -- CSS rotation values, not user-facing */
const FRAME_STYLES: Array<{ rotation: string; zIndex: number }> = [
  { rotation: '-6deg', zIndex: 1 },
  { rotation: '0deg', zIndex: 2 },
  { rotation: '6deg', zIndex: 3 },
];
/* eslint-enable lingui/no-unlocalized-strings */

function SelectionStack({
  bookmarks,
  showThumbnails,
  badgeCount,
  allowPlaceholderFallback = true,
}: SelectionStackProps) {
  const frameCount = bookmarks.length;

  if (frameCount === 0) {
    return null;
  }

  return (
    <div className="selection-stack__imageStack" data-count={frameCount}>
      <div className="selection-stack__glow" />
      {bookmarks.map((bookmark, index) => {
        const style =
          frameCount === 1
            ? { rotation: '0deg' as const, zIndex: 1 }
            : FRAME_STYLES[index];
        return (
          <FrameContent
            key={bookmark.id}
            bookmark={bookmark}
            showThumbnails={showThumbnails}
            allowPlaceholderFallback={allowPlaceholderFallback}
            rotation={style.rotation}
            zIndex={style.zIndex}
          />
        );
      })}
      {typeof badgeCount === 'number' && badgeCount > 0 && (
        <div className="selection-stack__badge" aria-hidden>
          {badgeCount}
        </div>
      )}
    </div>
  );
}

interface FrameContentProps {
  bookmark: Bookmark;
  showThumbnails: boolean;
  allowPlaceholderFallback: boolean;
  rotation: string;
  zIndex: number;
}

function FrameContent({
  bookmark,
  showThumbnails,
  allowPlaceholderFallback,
  rotation,
  zIndex,
}: FrameContentProps) {
  const [imgError, setImgError] = useState(false);
  const showImage = showThumbnails && bookmark.og_image && !imgError;
  const showPlaceholderFallback = allowPlaceholderFallback && !showImage;

  if (!showImage && !showPlaceholderFallback) {
    return null;
  }

  return (
    <div
      className="selection-stack__frame"
      style={
        {
          '--rotation': rotation,
          '--z-index': zIndex,
        } as CSSProperties
      }
    >
      {showImage ? (
        <img
          src={bookmark.og_image}
          alt=""
          className="selection-stack__image"
          onError={() => setImgError(true)}
        />
      ) : (
        <div
          className="flex h-full w-full items-center justify-center"
          style={{ backgroundColor: 'var(--bg-placeholder)' }}
        >
          <ThumbnailPlaceholderIcon />
        </div>
      )}
    </div>
  );
}

export default SelectionStack;
